import React, { useState, useEffect } from "react";

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [animateAccuracy, setAnimateAccuracy] = useState(false);

  useEffect(() => {
    // Trigger accuracy animation after component mounts
    const timer = setTimeout(() => setAnimateAccuracy(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const AccuracyCircle = ({ percentage, label, color }) => {
    return (
      <div className="relative w-32 h-32 mx-auto mb-4">
        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="8"
          />
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 50}`}
            strokeDashoffset={animateAccuracy ? `${2 * Math.PI * 50 * (1 - percentage / 100)}` : `${2 * Math.PI * 50}`}
            className="transition-all duration-2000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className= "text-2xl text-black-600 font-bold" >
              {animateAccuracy ? `${percentage}%` : '0%'}
            </div>
            <div className="text-xs text-gray-500 font-medium">{label}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content - Full Width */}
      <div className="min-h-screen">
        {/* Content */}
        <main className="p-6 bg-white">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold text-gray-800 mb-2 tracking-tight">
              ML MODEL TO PREDICT PAYMENT DEFAULTS
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-blue-500 to-purple-600 mx-auto rounded-full mb-2"></div>
            <p className="text-lg text-gray-600 font-medium">Performance of the Model</p>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Independent Variables */}
            <div className="lg:col-span-3 flex justify-center">
              <div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 h-fit w-full max-w-sm">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-6 h-6 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">INDEPENDENT VARIABLES</h3>
                </div>
                <div className="space-y-1">
                  {[
                    "Amount Repaid", "Average Distance Travelled", "Digital Footprint", 
                    "Downpayment", "Gender", "Identity Confidence", "Loan Amount", 
                    "Monthly EMI Amount", "Own Residence", "Remaining Amount", 
                    "Risk Score", "Social Footprint Score", "Tenure of EMI", "Vehicle Status"
                  ].map((variable, index) => (
                    <div key={index} className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-50 transition-all duration-200 group">
                      <div className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full group-hover:scale-125 transition-transform duration-200"></div>
                      <span className="text-m text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{variable}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Confusion Matrices */}
            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Old Version Matrix */}
                <div className="bg-white rounded-xl p-1 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="text-center mb-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">CONFUSION MATRIX</h3>
                    <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-xs font-medium">July 2024</span>
                  </div>
                  
                  {/* Matrix Container with Accuracy */}
                  <div className="flex justify-center items-center gap-4 mb-4">
                    <div className="w-full max-w-xs">
                      {/* Column Headers */}
                      <div className="grid grid-cols-3 gap-0.5 mb-1">
                        <div></div>
                        <div className="text-center text-xs font-medium text-gray-600 p-1">
                          Predicted On<br/>Time Payment
                        </div>
                        <div className="text-center text-xs font-medium text-gray-600 p-1">
                          Predicted<br/>Delayed
                        </div>
                      </div>
                      
                      {/* First Row */}
                      <div className="grid grid-cols-3 gap-0.5 mb-0.5">
                        <div className="text-center text-xs font-medium text-gray-600 p-1 flex items-center justify-center">
                          <div className="transform -rotate-90 whitespace-nowrap text-xs">
                            Actual On<br/>Time Payment
                          </div>
                        </div>
                        <div className="bg-green-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">89</div>
                          <div className="text-xs text-green-50">True Positive</div>
                        </div>
                        <div className="bg-yellow-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">16</div>
                          <div className="text-xs text-yellow-50">False Positive</div>
                        </div>
                      </div>
                      
                      {/* Second Row */}
                      <div className="grid grid-cols-3 gap-0.5">
                        <div className="text-center text-xs font-medium text-gray-600 p-1 flex items-center justify-center">
                          <div className="transform -rotate-90 whitespace-nowrap text-xs">
                            Actual<br/>Delayed
                          </div>
                        </div>
                        <div className="bg-red-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">20</div>
                          <div className="text-xs text-red-50">False Negative</div>
                        </div>
                        <div className="bg-blue-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">91</div>
                          <div className="text-xs text-blue-50">True Negative</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Model Accuracy - Right Side */}
                    <div className="text-center">
                      <AccuracyCircle percentage={83} label="Accuracy" color="blue" />
                    </div>
                  </div>
                </div>

                {/* New Version Matrix */}
                <div className="bg-white rounded-xl p-1 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
                  <div className="text-center mb-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">CONFUSION MATRIX</h3>
                    <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">July 2025</span>
                  </div>
                  
                  {/* Matrix Container with Accuracy */}
                  <div className="flex justify-center items-center gap-4 mb-4">
                    <div className="w-full max-w-xs">
                      {/* Column Headers */}
                      <div className="grid grid-cols-3 gap-0.5 mb-1">
                        <div></div>
                        <div className="text-center text-xs font-medium text-gray-600 p-1">
                          Predicted On<br/>Time Payment
                        </div>
                        <div className="text-center text-xs font-medium text-gray-600 p-1">
                          Predicted<br/>Delayed
                        </div>
                      </div>
                      
                      {/* First Row */}
                      <div className="grid grid-cols-3 gap-0.5 mb-0.5">
                        <div className="text-center text-xs font-medium text-gray-600 p-1 flex items-center justify-center">
                          <div className="transform -rotate-90 whitespace-nowrap text-xs">
                            Actual On<br/>Time Payment
                          </div>
                        </div>
                        <div className="bg-green-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">282</div>
                          <div className="text-xs text-green-50">True Positive</div>
                        </div>
                        <div className="bg-yellow-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">11</div>
                          <div className="text-xs text-yellow-50">False Positive</div>
                        </div>
                      </div>
                      
                      {/* Second Row */}
                      <div className="grid grid-cols-3 gap-0.5">
                        <div className="text-center text-xs font-medium text-gray-600 p-1 flex items-center justify-center">
                          <div className="transform -rotate-90 whitespace-nowrap text-xs">
                            Actual<br/>Delayed
                          </div>
                        </div>
                        <div className="bg-red-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">67</div>
                          <div className="text-xs text-red-50">False Negative</div>
                        </div>
                        <div className="bg-blue-500 rounded-lg p-3 text-center hover:scale-105 transition-transform duration-200 cursor-pointer shadow-md aspect-square flex flex-col justify-center">
                          <div className="text-xl font-bold text-white mb-0.5">274</div>
                          <div className="text-xs text-blue-50">True Negative</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Model Accuracy - Right Side */}
                    <div className="text-center">
                      <AccuracyCircle percentage={88} label="Accuracy" color="green" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Patent Information */}
              <div className="mt-2 bg-white rounded-xl p-2 border border-gray-200 shadow-sm">
                <div className="text-center mb-1">
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <h3 className="text-lg font-semibold text-gray-800">PATENT FILLING DETAILS</h3>
                  </div>
                </div>
                <div className="max-w-4xl mx-auto">
                  <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
                    <img 
                      src="./patent-details.png" 
                      alt="Patent Filing Details Table" 
                      className="w-full h-auto"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div style={{display: 'none', padding: '2rem', textAlign: 'center', color: '#6b7280'}}>
                      Image not found. Please place your screenshot in the public folder as 'patent-details.png'
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New Correlation Matrix Section - Full Width */}
          <div className="mt-2 bg-white rounded-xl p-1 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="text-center mb-1">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <h3 className="text-lg font-semibold text-gray-800">CORRELATION MATRIX</h3>
              </div>
            </div>
            
            <div className="flex justify-center">
              <div className="max-w-full overflow-hidden rounded-lg border border-gray-300 shadow-lg">
                <img 
                  src="./correlation-matrix.png" 
                  alt="Correlation Matrix Heatmap showing relationships between variables" 
                  className="w-full h-auto max-w-4xl"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div style={{display: 'none', padding: '3rem', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb'}}>
                  <div className="text-lg font-medium mb-2">Correlation Matrix Image Not Found</div>
                  <div className="text-sm">Please place your correlation matrix image in the public folder as 'correlation-matrix.png'</div>
                </div>
              </div>
            </div>
            
            {/* Optional: Add some descriptive text about the correlation matrix */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 max-w-3xl mx-auto">
                This correlation matrix visualizes the relationships between all independent variables used in the ML model. 
                The color intensity represents the strength of correlation, with darker colors indicating stronger relationships.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;